import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { docReadDemo } from '@/lib/docRead';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { toast } from 'sonner';
import { AlertTriangle, Check, FileScan, Loader2 } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

const readFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ mediaType: file.type, data: String(reader.result).split(',')[1] });
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

// Photo of a CMR, weighbridge ticket or delivery note → fields checked against the loading (#70).
export const DocumentReader = ({ loading, carrier, onAttach }) => {
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null); // { mode, result, attached }
  const info = { plate: loading.plate, orderId: loading.orderId, weight: loading.weight, carrier: carrier.name };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 4_800_000) { toast.error(t('ai.doc.tooLarge')); return; }
    setBusy(true);
    const res = await runAi('doc-read', { ...(await readFile(file)), loading: info }, docReadDemo);
    setDoc({ ...res, attached: false });
    setBusy(false);
  };

  const r = doc?.result;
  const problems = r ? r.checks.filter(c => !c.ok) : [];
  const attach = () => {
    const summary = `${t(`ai.doc.type.${r.docType}`)}: ${problems.length
      ? problems.map(p => `${t(`ai.doc.field.${p.field}`)} ${p.onDoc} ≠ ${p.expected}`).join(', ')
      : t('ai.doc.allMatch')}`;
    onAttach(loading.id, summary);
    setDoc({ ...doc, attached: true });
  };

  return (
    <div className="rounded-xl border border-dashed p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
        <FileScan className="h-4 w-4 text-primary" /> {t('ai.doc.title')} <HelpTip id="doc" className="normal-case" />
        {doc && <AiBadge mode={doc.mode} className="ml-auto normal-case" />}
      </div>
      <Button variant="outline" size="sm" className="gap-1" disabled={busy} asChild>
        <label className="cursor-pointer">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileScan className="h-3.5 w-3.5" />} {t('ai.doc.upload')}
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" className="hidden" onChange={onFile} aria-label={t('ai.doc.upload')} />
        </label>
      </Button>
      {r && (
        <div className="space-y-2 text-sm" aria-live="polite">
          <div className="font-medium">{t(`ai.doc.type.${r.docType}`)}</div>
          <ul className="space-y-1">
            {r.checks.map(c => (
              <li key={c.field} className={cn('flex items-center gap-2', !c.ok && 'text-red-700')}>
                {c.ok ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                {t(`ai.doc.field.${c.field}`)}: {c.onDoc}{!c.ok && ` (${t('ai.doc.expected')} ${c.expected})`}
              </li>
            ))}
          </ul>
          {r.notes && <div className="text-xs text-muted-foreground">{r.notes}</div>}
          {doc.attached
            ? <div className="text-xs text-emerald-700">{t('ai.doc.attached')}</div>
            : <Button size="sm" variant="outline" onClick={attach}>{t('ai.doc.attach')}</Button>}
        </div>
      )}
    </div>
  );
};
